import React, { useEffect, useState } from "react";

import { Icon } from "@iconify/react";
import { cn } from "lib/utils";
import { Loader2, MoreVertical } from "lucide-react";

import {
  useDownloadCreditReportMetadata,
  useDownloadInvestigationCreditReport,
  useDownloadInvestigationMultireport,
  useInvestigations,
  useRetryLongReport,
  useSendCreditReport,
  useSendInvestigationMultireport,
  useSendMetadata,
  useToggleInvestigationStatus,
} from "hooks/useInvestigations";

import ShowInfoModal from "js/components/Dashboard/ShowInfoModal";
import { Investigation } from "js/components/ExpressReport/types";
import { PaginationTable, Person } from "js/components/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "js/components/ui/alert-dialog";
import { Badge } from "js/components/ui/badge";
import { Button } from "js/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "js/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "js/components/ui/command";
import { TablePagination } from "js/components/ui/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "js/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "js/components/ui/tooltip";

interface InvestigationCollapseProps {
  showAdvisorColumn: Boolean;
  searchFilter?: string;
  isOrganization?: boolean;
  additionalFilters?: {
    is_active?: string;
    status?: string;
    legal_type?: string;
  };
}

const InvestigationCardList: React.FC<InvestigationCollapseProps> = ({
  showAdvisorColumn,
  searchFilter = "",
  isOrganization = false,
  additionalFilters = {},
}) => {
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 6,
    total: 0,
  });

  // Fetch investigations using TanStack Query
  const { data: investigationsData, isLoading } = useInvestigations({
    pagination,
    filter: searchFilter,
    additionalFilters,
    isOrganization,
  });

  // Mutations for actions
  const toggleStatusMutation = useToggleInvestigationStatus();
  const retryLongReportMutation = useRetryLongReport();
  const downloadMultireportMutation = useDownloadInvestigationMultireport();
  const downloadCreditReportMutation = useDownloadInvestigationCreditReport();
  const downloadMetadataMutation = useDownloadCreditReportMetadata();
  const sendMultireportMutation = useSendInvestigationMultireport();
  const sendCreditReportMutation = useSendCreditReport();
  const sendMetadataMutation = useSendMetadata();

  // Local state for actions
  const [infoModalInvestigation, setInfoModalInvestigation] =
    useState<Investigation | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [investigationToToggle, setInvestigationToToggle] =
    useState<Investigation | null>(null);

  // Derive loading states from mutations
  const isDownloading =
    downloadMultireportMutation.isPending ||
    downloadCreditReportMutation.isPending ||
    downloadMetadataMutation.isPending;

  const isSending =
    sendMultireportMutation.isPending ||
    sendCreditReportMutation.isPending ||
    sendMetadataMutation.isPending;

  // Update pagination total when data changes
  useEffect(() => {
    if (!investigationsData?.count && investigationsData?.count !== 0) return;

    setPagination((prev) => {
      const total = investigationsData.count;
      const totalPages = Math.max(1, Math.ceil(total / prev.pageSize));
      const nextCurrent = Math.min(prev.current, totalPages);
      if (prev.total === total && prev.current === nextCurrent) return prev;
      return {
        ...prev,
        total,
        current: nextCurrent,
      };
    });
  }, [investigationsData?.count]);

  const data = investigationsData?.results ?? [];

  const getReportStatus = (investigation: Investigation) => {
    if (
      investigation.status === "in_progress" &&
      investigation.credit_report.status === "capturing_inputs"
    ) {
      return "Capturando Información";
    }

    switch (investigation.credit_report?.status) {
      case "capturing_inputs":
        return "Iniciado";
      case "pin_validation":
        return "Confirmando NIP";
      case "completed":
        return "Completado";
      case "long_report":
        return "Esperando Reporte Extenso";
      case "invalid":
        return "Invalidado";
      default:
        return "Estado Desconocido";
    }
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      total: investigationsData?.count ?? prev.total,
    }));
  };

  const handleDeactivate = async () => {
    if (!investigationToToggle) return;
    await toggleStatusMutation.mutateAsync({
      investigationId: investigationToToggle.id,
      action: "deactivate",
    });
    setConfirmOpen(false);
    setInvestigationToToggle(null);
  };

  const renderInvestigationCard = (investigation: Investigation) => {
    const person: Person | undefined = investigation.person;
    const completed = investigation.status === "completed";
    const waitingLong = investigation.status === "Waiting long Report";
    const normalized = (investigation.legal_type || "").toLowerCase();
    const isMoral = normalized === "legal" || normalized === "moral";

    const personName =
      person?.type === "Legal"
        ? person.legal_entity_name
        : [person?.name, person?.first_surname, person?.second_surname]
            .filter(Boolean)
            .join(" ");

    const sections = [
      {
        heading: "Acceso",
        items: [
          {
            key: "link-report",
            label: "Abrir link de reporte",
            icon: "solar:link-circle-linear",
          },
          {
            key: "view-info",
            label: "Ver información",
            icon: "ion:information-circle-outline",
          },
          {
            key: "retry",
            label: "Actualizar Reporte Largo",
            icon: "tabler:refresh",
            disabled: !waitingLong,
          },
        ],
      },
      {
        heading: "Estado",
        items: [
          {
            key: investigation.is_active ? "deactivate" : "activate",
            label: investigation.is_active ? "Desactivar" : "Activar",
            icon: investigation.is_active
              ? "solar:power-bold"
              : "mdi:power-cycle",
            disabled: completed,
          },
        ],
      },
      {
        heading: "Descargas",
        items: [
          {
            key: "download-multi",
            label: "Descargar Multireporte",
            icon: "ion:cloud-download-outline",
            disabled: !completed || isDownloading,
          },
          {
            key: "download-hc",
            label: "Descargar Historial Crediticio",
            icon: "ion:document-text-outline",
            disabled: !completed || isDownloading,
          },
          {
            key: "download-metadata",
            label: "Descargar Metadata",
            icon: "ion:server-outline",
            disabled: !completed || isDownloading,
          },
        ],
      },
      {
        heading: "Enviar",
        items: [
          {
            key: "send-multi",
            label: "Enviar Multireporte",
            icon: "ion:send-outline",
            disabled: !completed || isSending,
          },
          {
            key: "send-hc",
            label: "Enviar Historial Crediticio",
            icon: "ion:send-outline",
            disabled: !completed || isSending,
          },
          {
            key: "send-metadata",
            label: "Enviar Metadata",
            icon: "ion:send-outline",
            disabled: !completed || isSending,
          },
        ],
      },
    ];

    const handleSelect = async (key: string) => {
      if (key === "activate") {
        await toggleStatusMutation.mutateAsync({
          investigationId: investigation.id,
          action: "activate",
        });
      } else if (key === "deactivate") {
        setInvestigationToToggle(investigation);
        setConfirmOpen(true);
      } else if (key === "link-report") {
        const w = window.open(
          `/reports/${investigation.id}`,
          "_blank",
          "noopener,noreferrer",
        );
        if (w) w.opener = null;
      } else if (key === "view-info") {
        setInfoModalInvestigation(investigation);
      } else if (key === "retry") {
        await retryLongReportMutation.mutateAsync(investigation.id);
      } else if (key === "download-multi") {
        downloadMultireportMutation.mutate(investigation.id);
      } else if (key === "download-hc") {
        downloadCreditReportMutation.mutate(investigation.id);
      } else if (key === "download-metadata") {
        downloadMetadataMutation.mutate(investigation.id);
      } else if (key === "send-multi") {
        sendMultireportMutation.mutate(investigation.id);
      } else if (key === "send-hc") {
        sendCreditReportMutation.mutate(investigation.id);
      } else if (key === "send-metadata") {
        sendMetadataMutation.mutate(investigation.id);
      }
    };

    return (
      <Card key={investigation.id} className="bg-white shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center">
                      <Icon
                        icon={
                          isMoral
                            ? "octicon:organization-24"
                            : "octicon:person-24"
                        }
                        fontSize={24}
                        className="text-slate-600"
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {isMoral ? "Persona Moral" : "Persona Física"}
                  </TooltipContent>
                </Tooltip>
                <Badge
                  variant="outline"
                  className={cn(
                    "border-emerald-200 text-emerald-600",
                    isMoral && "border-sky-200 text-sky-600",
                  )}
                >
                  {isMoral ? "Moral" : "Física"}
                </Badge>
              </div>
              <CardTitle className="text-base leading-tight">
                {personName || "Sin nombre"}
              </CardTitle>
              <p className="text-xs text-slate-500">
                {investigation.created_at
                  ? new Date(investigation.created_at).toLocaleDateString(
                      "es-ES",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )
                  : ""}
              </p>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center">
                  <Icon
                    icon={
                      investigation.is_active
                        ? "ion:checkmark-circle"
                        : "ion:close-circle"
                    }
                    fontSize={20}
                    color={investigation.is_active ? "#10b981" : "#d4d4d8"}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">
                {investigation.is_active ? "Activo" : "Inactivo"}
              </TooltipContent>
            </Tooltip>
          </div>
        </CardHeader>

        <CardContent className="space-y-2.5 text-sm">
          {showAdvisorColumn && (
            <div className="flex justify-between">
              <span className="font-medium text-slate-600">Asesor:</span>
              <span className="text-slate-900">
                {investigation.advisor?.full_name?.trim() || "Huérfano"}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="font-medium text-slate-600">Correo:</span>
            <span className="line-clamp-1 text-right text-slate-900">
              {investigation.prospectus_email || "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-slate-600">Fase:</span>
            <span className="text-slate-900">
              {getReportStatus(investigation)}
            </span>
          </div>
        </CardContent>

        <CardFooter className="pt-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2 text-slate-600"
              >
                <MoreVertical className="size-4" />
                Acciones
              </Button>
            </PopoverTrigger>
            <PopoverContent align="center" className="w-64 p-0">
              <Command>
                <CommandList className="max-h-none">
                  <CommandEmpty>Sin acciones disponibles</CommandEmpty>
                  {sections.map((section, index) => (
                    <React.Fragment key={section.heading}>
                      {index > 0 && <CommandSeparator />}
                      <CommandGroup heading={section.heading}>
                        {section.items.map((item) => (
                          <CommandItem
                            key={item.key}
                            value={item.key}
                            disabled={item.disabled}
                            onSelect={async (current) => {
                              await handleSelect(current);
                            }}
                            className="cursor-pointer"
                          >
                            <Icon
                              icon={item.icon}
                              fontSize={16}
                              className="text-slate-400"
                            />
                            {item.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </React.Fragment>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </CardFooter>
      </Card>
    );
  };

  return (
    <>
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
            <Loader2 className="size-6 animate-spin" />
            <span>Cargando investigaciones...</span>
          </div>
        ) : data && data.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            No se encontraron investigaciones.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {data?.map((investigation) =>
                renderInvestigationCard(investigation),
              )}
            </div>
            <TablePagination
              pagination={pagination}
              onChange={handlePageChange}
              isLoading={isLoading}
            />
          </>
        )}
      </div>

      {infoModalInvestigation && (
        <ShowInfoModal
          report={infoModalInvestigation}
          isModalVisible={true}
          setIsModalVisible={(v: boolean) => {
            if (!v) setInfoModalInvestigation(null);
          }}
        />
      )}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmOpen(false);
            setInvestigationToToggle(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar investigación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que deseas desactivar esta investigación? Los cobros
              hechos no son reembolsables.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={handleDeactivate}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default InvestigationCardList;
